import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { reviseHistoryLog } from "apis/controllers/applications";
import { FileGroup } from "common/FileUpload";
import { FileProvider, useFileContext } from "context/FilesV2";
import { useLoading } from "hooks/useLoading";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { deleteBundle, deleteFile, downloadFile, uploadFiles } from "util";
import { v4 as uuidv4 } from "uuid";

const PROJECT_TABLE_ID = 4;

const createHistoryFileGroup = (row) => {
  if (row?.fileTypeId == null) return [];

  return [
    {
      id: row.fileTypeId,
      title: row?.fileTypeDescr ?? "Upload file",
      fields: [
        {
          type: "string",
          name: "comments",
          label: row?.commentsLabel ?? row?.lblComments ?? "Σχόλια",
          isRequired: false,
        },
      ],
      bundles: [
        {
          id: uuidv4(),
          files: [],
          values: { comments: row?.comments ?? "" },
          saved: false,
        },
      ],
      templates: [],
      maxBundles: 1,
    },
  ];
};

const getBundleIds = (fileGroups) =>
  fileGroups.reduce((acc, group) => {
    return [
      ...acc,
      ...(group?.bundles ?? [])
        .filter((bundle) => bundle?.files?.length > 0)
        .map((bundle) => bundle.id),
    ];
  }, []);

const hasSelectedFiles = (fileGroups) =>
  fileGroups.some((group) =>
    group?.bundles?.some((bundle) => bundle?.files?.length > 0)
  );

const getLogId = (row) => row?.id ?? row?.logId ?? row?.eventLogId;

const getComment = (fileGroups) =>
  fileGroups
    .flatMap((group) => group?.bundles ?? [])
    .find((bundle) => bundle?.files?.length > 0)
    ?.values?.comments ?? null;

const HistoryFileUploadDialog = ({
  open,
  row,
  applicationId,
  projectTableId = PROJECT_TABLE_ID,
  onClose,
  onSuccess,
}) => {
  const recordGuid = applicationId ?? null;

  return (
    <FileProvider
      downloadFile={(fileId, type) => downloadFile(fileId, type)}
      uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
      onDeleteBundle={async (bundleId) =>
        await deleteBundle(bundleId, projectTableId, null, recordGuid, null)
      }
      onDeleteFile={async (fileId) => {
        await deleteFile(fileId, projectTableId, null, recordGuid, null);
      }}
    >
      <HistoryFileUploadDialogContent
        open={open}
        row={row}
        projectTableId={projectTableId}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </FileProvider>
  );
};

const HistoryFileUploadDialogContent = ({
  open,
  row,
  projectTableId,
  onClose,
  onSuccess,
}) => {
  const { fileGroups, control } = useFileContext();
  const { setLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!open || !row) {
      control.initialize([]);
      return;
    }

    control.initialize(createHistoryFileGroup(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  const handleClose = () => {
    control.initialize([]);
    onClose();
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const logId = getLogId(row);
      if (!logId) {
        enqueueSnackbar("Δε βρέθηκε το αναγνωριστικό του ιστορικού.", {
          variant: "error",
        });
        return;
      }

      const uploadResponse = await control.uploadFileGroups();
      if (uploadResponse?.status === "error") {
        enqueueSnackbar(
          uploadResponse?.message ?? "Η μεταφόρτωση του αρχείου απέτυχε.",
          { variant: "error" }
        );
        return;
      }

      const bundleIds = getBundleIds(fileGroups);
      const res = await reviseHistoryLog({
        LogId: logId,
        BundleIds: bundleIds,
        Comment: getComment(fileGroups),
        projectTableId,
      });

      if (res?.status === "error" || res?.succeeded === false) {
        enqueueSnackbar(
          res?.message ?? "Η ορθή επανάληψη δεν πραγματοποιήθηκε.",
          { variant: "error" }
        );
        return;
      }

      control.setIsDirty(false);
      await onSuccess?.();
      handleClose();
      if (res?.message) enqueueSnackbar(res.message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Η ορθή επανάληψη δεν πραγματοποιήθηκε.",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="h4">{row?.fileUploadTitle ?? "Μεταφόρτωση αρχείου"}</Typography>
      </DialogTitle>
      <DialogContent>
        {fileGroups?.map((group) => (
          <FileGroup
            key={group.id}
            reviewMode={false}
            isReadOnly={false}
            canAdd
            id={group.id}
            title={group?.title}
            maxSize={row?.maxSize ?? 6291456}
            maxFiles={row?.maxFiles ?? 5}
            maxBundles={1}
            fields={group.fields}
            bundles={group?.bundles}
            templates={group?.templates}
            accept={row?.accept ?? null}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error" variant="contained">
          Ακύρωση
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!hasSelectedFiles(fileGroups)}
        >
          Αποθήκευση
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryFileUploadDialog;
